import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DynamicModal from '../../components/Modal/DynamicModal';
import './UpdateForm.css';
import { BASE_URL } from '../../config';
import { getAccessToken } from '../LoginForm/Auth/auth';

const WFS_URL = `${BASE_URL}/geoserver/ows?`;

const UpdateForm = ({
  activeMode,
  currentDrawing,
  handleUpdateAttributeWrapper,
  handleUpdateGeometryWrapper,
  setPopupInfo,
  setCurrentDrawing,
  isOpen,
  onRequestClose,
  typeName
}) => {
  const [properties, setProperties] = useState({});
  const [geometryCoordinates, setGeometryCoordinates] = useState('');
  const [attributeTypes, setAttributeTypes] = useState({});
  const [primaryKeyFields, setPrimaryKeyFields] = useState([]);

  const [selectOptions, setSelectOptions] = useState([]);
  const [topoKeyMap, setTopoKeyMap] = useState([]);
  const [terenskeJedinice, setTerenskeJedinice] = useState([]);
  const [dynamicEnumOptions, setDynamicEnumOptions] = useState({});

  const token = getAccessToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const disabledFields = [
    'koordinata_x',
    'koordinata_y',
    'y_koordinata_dks',
    'x_koordinata_dks',
    'x_start',
    'x_end',
    'y_start',
    'y_end',
    'sifra_topografski_kljuc',
    'id_terenske_jedinice'
  ];

  useEffect(() => {
    if (currentDrawing?.properties) {
      setProperties(currentDrawing.properties);
    }

    if (currentDrawing?.geometry?.coordinates) {
      setGeometryCoordinates(JSON.stringify(currentDrawing.geometry.coordinates));
    }
  }, [currentDrawing]);

  useEffect(() => {
    if (typeName) {
      fetchAttributeTypes(typeName);
    }
  }, [typeName]);

  const fetchAttributeTypes = async (layerName) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/geoserver/wfs?service=WFS&version=1.1.0&request=DescribeFeatureType&typeName=${layerName}`,
        { headers }
      );

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(response.data, 'text/xml');
      const attributes = {};
      const primKeys = [];

      xmlDoc.querySelectorAll('xsd\\:element, element').forEach(el => {
        const name = el.getAttribute('name');
        const type = el.getAttribute('type') || '';

        if (/string/i.test(type)) attributes[name] = 'text';
        else if (/date|time/i.test(type)) attributes[name] = 'date';
        else if (/int|double|float/i.test(type)) attributes[name] = 'number';
        else attributes[name] = 'text';

        if (name?.toLowerCase().startsWith('id_') || name === 'id') {
          primKeys.push(name);
        }
      });

      if (layerName.includes('ocitacki_hod')) {
        primKeys.push('id_mm');
      }

      setAttributeTypes(attributes);
      setPrimaryKeyFields(primKeys);
    } catch (error) {
      console.error('Greška pri dohvatanju tipova atributa:', error);
    }
  };

  useEffect(() => {
    if (!currentDrawing?.properties || !Object.keys(attributeTypes).length) return;

    const formattedProperties = { ...currentDrawing.properties };

    Object.keys(formattedProperties).forEach(key => {
      if (attributeTypes[key] === 'date' && formattedProperties[key]) {
        const rawDate = formattedProperties[key];

        if (typeof rawDate === 'string' && rawDate.includes('T')) {
          formattedProperties[key] = rawDate.split('T')[0];
        } else if (!/^\d{4}-\d{2}-\d{2}$/.test(String(rawDate))) {
          const date = new Date(rawDate);

          if (!isNaN(date.getTime())) {
            formattedProperties[key] = date.toISOString().split('T')[0];
          } else {
            formattedProperties[key] = '';
          }
        }
      }
    });

    setProperties(formattedProperties);
  }, [currentDrawing, attributeTypes]);

  useEffect(() => {
    const propKeys = Object.keys(properties);

    if (propKeys.includes('naziv_topografski_kljuc') || propKeys.includes('sifra_topografski_kljuc')) {
      fetchSelectOptions(propKeys);
    }

    if (propKeys.includes('terenska_jedinica') || propKeys.includes('id_terenske_jedinice')) {
      fetchTerenskeJedinice();
    }
  }, [properties]);

  const fetchSelectOptions = async (propKeys) => {
    let tipObjekta = 'ostalo';

    if (propKeys.includes('id_stubnog_mjesta')) {
      tipObjekta = 'stub';
    } else if (propKeys.includes('id_trafo_stanice')) {
      tipObjekta = 'trafo_stanica';
    }

    try {
      const response = await axios.get(WFS_URL, {
        params: {
          service: 'WFS',
          version: '1.0.0',
          request: 'GetFeature',
          typeName: 'cl_topografski_kljuc',
          outputFormat: 'application/json',
          CQL_FILTER: `tip_objekta='${tipObjekta}'`
        },
        headers
      });

      const nazivIdParovi = response.data.features
        ?.filter(f => f.properties.naziv && f.properties.id_topografski_kljuc)
        .map(f => ({
          naziv: f.properties.naziv,
          id: f.properties.id_topografski_kljuc
        })) || [];

      setTopoKeyMap(nazivIdParovi);
      setSelectOptions(nazivIdParovi.map(i => i.naziv));
    } catch (e) {
      console.error('Greška pri dohvatanju topografskih ključeva:', e);
    }
  };

  const fetchTerenskeJedinice = async () => {
    try {
      const response = await axios.get(WFS_URL, {
        params: {
          service: 'WFS',
          version: '1.0.0',
          request: 'GetFeature',
          typeName: 'cl_terenske_jedinice',
          outputFormat: 'application/json'
        },
        headers
      });

      const jedinice = response.data.features
        ?.map(f => ({
          id: f.properties.ID,
          naziv: f.properties.Naziv
        }))
        .filter(j => j.id && j.naziv) || [];

      setTerenskeJedinice(jedinice);
    } catch (e) {
      console.error('Greška pri dohvaćanju terenskih jedinica:', e);
    }
  };

  const fetchMatchingClTable = async (attributeName) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/geoserver/wfs?service=WFS&version=1.1.0&request=GetCapabilities`,
        { headers }
      );

      const xml = new DOMParser().parseFromString(response.data, 'text/xml');

      const featureTypes = Array.from(xml.getElementsByTagName('FeatureType'))
        .map(ft => ft.getElementsByTagName('Name')[0]?.textContent)
        .filter(name => name && name.includes('cl_'));

      const normalizedAttr = attributeName.toLowerCase().replace(/_/g, '');

      const matched = featureTypes.find(name => {
        const localName = name.includes(':') ? name.split(':')[1] : name;

        return localName
          .toLowerCase()
          .replace('cl_', '')
          .replace(/_/g, '')
          .includes(normalizedAttr);
      });

      return matched || null;
    } catch (error) {
      console.error('Greška pri dohvaćanju lista slojeva:', error);
      return null;
    }
  };

  const fetchValuesFromClTable = async (tableName, attribute) => {
    try {
      const response = await axios.get(WFS_URL, {
        params: {
          service: 'WFS',
          version: '1.0.0',
          request: 'GetFeature',
          typeName: tableName,
          outputFormat: 'application/json'
        },
        headers
      });

      const values = response.data.features
        ?.map(f => f.properties?.naziv || f.properties?.Naziv)
        .filter(Boolean) || [];

      if (values.length) {
        setDynamicEnumOptions(prev => ({
          ...prev,
          [attribute]: values
        }));
      }
    } catch (e) {
      console.warn(`Greška pri dohvaćanju vrijednosti iz ${tableName}:`, e);
    }
  };

  useEffect(() => {
    const propKeys = Object.keys(properties);

    if (!propKeys.length) return;

    const excluded = [
      ...disabledFields.map(f => f.toLowerCase()),
      ...primaryKeyFields.map(f => f.toLowerCase()),
      'naziv_topografski_kljuc',
      'sifra_topografski_kljuc',
      'id_terenske_jedinice',
      'terenska_jedinica',
      'tip_mreze',
      'vrsta_mreze',
      'tip_mreza',
      'vrsta_mreza'
    ];

    propKeys.forEach(async attr => {
      if (excluded.includes(attr.toLowerCase())) return;

      const tbl = await fetchMatchingClTable(attr);

      if (tbl) {
        fetchValuesFromClTable(tbl, attr);
      }
    });
  }, [properties, primaryKeyFields]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    setProperties(prevProps => {
      const updated = {
        ...prevProps,
        [name]: type === 'date' && value
          ? new Date(value).toISOString().split('T')[0]
          : value
      };

      if (name === 'naziv_topografski_kljuc') {
        const found = topoKeyMap.find(i => i.naziv === value);
        updated.sifra_topografski_kljuc = found ? found.id : '';
      }

      if (name === 'sifra_topografski_kljuc') {
        const found = topoKeyMap.find(i => String(i.id) === String(value));

        if (found) {
          updated.naziv_topografski_kljuc = found.naziv;
        }
      }

      if (name === 'terenska_jedinica') {
        const selected = terenskeJedinice.find(i => i.naziv === value);
        updated.id_terenske_jedinice = selected ? selected.id : '';
      }

      return updated;
    });
  };

  const handleGeometryChange = (e) => {
    setGeometryCoordinates(e.target.value);
  };

  const isNetworkField = (key) => {
    const lower = key.toLowerCase();

    return (
      lower === 'tip_mreze' ||
      lower === 'vrsta_mreze' ||
      lower === 'tip_mreza' ||
      lower === 'vrsta_mreza' ||
      lower.includes('tip_mreze') ||
      lower.includes('vrsta_mreze')
    );
  };

  const isFieldDisabled = (key) => {
    const lower = key.toLowerCase();

    return (
      disabledFields.includes(lower) ||
      primaryKeyFields.includes(key) ||
      (typeName?.includes('ocitacki_hod') && key === 'id_mm') ||
      isNetworkField(key)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (activeMode === 'updateAttribute') {
      const cleanedProps = Object.entries(properties).reduce((acc, [key, val]) => {
        const expectedType = attributeTypes[key];

        if (expectedType === 'number') {
          const num = Number(val);

          if (!isNaN(num)) {
            acc[key] = num;
          }
        } else if (expectedType === 'date') {
          if (val && !isNaN(new Date(val).getTime())) {
            acc[key] = new Date(val).toISOString().substring(0, 10);
          }
        } else {
          if (val !== undefined && val !== null && val !== '') {
            acc[key] = val;
          }
        }

        return acc;
      }, {});

      handleUpdateAttributeWrapper(cleanedProps);

    } else if (activeMode === 'updateGeometry') {
      try {
        const parsedGeometry = JSON.parse(geometryCoordinates);
        handleUpdateGeometryWrapper(parsedGeometry);
      } catch (error) {
        console.error('Greška pri parsiranju JSON-a za geometriju:', error);
        return;
      }
    }

    closeModal();
  };

  const closeModal = () => {
    setPopupInfo(null);
    setCurrentDrawing([]);
    onRequestClose();
  };

  const renderInput = (key, value) => {
    const inputType = attributeTypes[key] || 'text';
    const disabled = isFieldDisabled(key);

    if (key === 'naziv_topografski_kljuc') {
      return (
        <select
          id={key}
          name={key}
          value={value || ''}
          onChange={handleInputChange}
          disabled={disabled}
          className="inputField"
        >
          <option value="">Odaberi...</option>
          {selectOptions.length
            ? selectOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))
            : <option disabled>Učitavam...</option>}
        </select>
      );
    }

    if (
      key === 'sifra_topografski_kljuc' &&
      !Object.prototype.hasOwnProperty.call(properties, 'naziv_topografski_kljuc')
    ) {
      return (
        <select
          id={key}
          name={key}
          value={value || ''}
          onChange={handleInputChange}
          disabled={false}
          className="inputField"
        >
          <option value="">Odaberi...</option>
          {topoKeyMap.length
            ? topoKeyMap.map((item, index) => (
              <option key={index} value={item.id}>
                {item.naziv} ({item.id})
              </option>
            ))
            : <option disabled>Učitavam...</option>}
        </select>
      );
    }

    if (key === 'terenska_jedinica') {
      return (
        <select
          id={key}
          name={key}
          value={value || ''}
          onChange={handleInputChange}
          disabled={false}
          className="inputField"
        >
          <option value="">Odaberi terensku jedinicu...</option>
          {terenskeJedinice.map((jedinica, index) => (
            <option key={index} value={jedinica.naziv}>
              {jedinica.naziv}
            </option>
          ))}
        </select>
      );
    }

    if (key === 'id_terenske_jedinice') {
      const selectedId =
        terenskeJedinice.find(j => j.naziv === properties.terenska_jedinica)?.id ||
        properties.id_terenske_jedinice ||
        '';

      return (
        <input
          autoComplete="off"
          type="text"
          id={key}
          name={key}
          value={selectedId}
          disabled
          readOnly
          className="inputField"
        />
      );
    }

    if (isNetworkField(key)) {
      return (
        <select
          id={key}
          name={key}
          value={value || ''}
          onChange={handleInputChange}
          disabled
          className="inputField"
        >
          <option value="">Odaberi...</option>
          <option value="niskonaponska">niskonaponska</option>
          <option value="srednjenaponska">srednjenaponska</option>
          <option value="visokonaponska">visokonaponska</option>
        </select>
      );
    }

    if (dynamicEnumOptions[key]?.length) {
      return (
        <select
          id={key}
          name={key}
          value={value || ''}
          onChange={handleInputChange}
          disabled={disabled}
          className="inputField"
        >
          <option value="">Odaberi...</option>
          {dynamicEnumOptions[key].map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        autoComplete="off"
        type={inputType}
        id={key}
        name={key}
        value={value || ''}
        onChange={handleInputChange}
        disabled={disabled}
        readOnly={disabled}
        className="inputField"
      />
    );
  };

  return (
    <DynamicModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      title={activeMode === 'updateAttribute' ? 'Ažuriranje atributa' : 'Ažuriranje geometrije'}
      customWidth={activeMode === 'updateAttribute' ? '55vw' : '40vw'}
      customHeight={activeMode === 'updateAttribute' ? '85vh' : '45vh'}
      mainContent={
        <form style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0px' }}>
          <div>
            {activeMode === 'updateAttribute' && Object.keys(properties).map(key => {
              const value = properties[key];
              const disabled = isFieldDisabled(key);

              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'transparent',
                    padding: '10px',
                    borderRadius: '5px'
                  }}
                >
                  <label
                    htmlFor={key}
                    style={{
                      color: disabled ? '#bbb' : '#fff'
                    }}
                  >
                    {key}:
                  </label>

                  {renderInput(key, value)}
                </div>
              );
            })}

            {activeMode === 'updateGeometry' && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'transparent',
                  padding: '10px',
                  borderRadius: '5px'
                }}
              >
                <label htmlFor="geometry">Koordinate:</label>
                <textarea
                  id="geometry"
                  value={geometryCoordinates}
                  onChange={handleGeometryChange}
                  rows={6}
                  required
                  className="textareaField"
                />
              </div>
            )}
          </div>
        </form>
      }
      footerContent={
        <>
          <button onClick={handleSubmit} className="app-btn app-btn-success">Sačuvaj</button>
          <button type="button" onClick={closeModal} className="app-btn app-btn-secondary">Zatvori</button>
        </>
      }
    />
  );
};

export default UpdateForm;